/**
 * Chart selection engine: validates/overrides backend chart type using data signals.
 * We change chart type by number of values so charts stay readable and appropriate.
 *
 * Value-based override rules (limits):
 * - 1 value → bar (override line/etc.; gauge is disabled; bar or pie work for single point)
 * - 2–6 categories → pie/donut allowed; <2 → bar; >6 → horizontal_bar
 * - >12 categories → bar → horizontal_bar; pie/donut → horizontal_bar
 * - ≥40 categories → pie/donut/radar/gauge/funnel/bar → horizontal_bar
 * - Gauge suggested but >1 value → bar or horizontal_bar
 * - Negative values + pie/treemap/funnel → bar
 *
 * Data trimming limits (e.g. max categories per type) are in chartDataAdjuster.
 */

/** Data signals we can derive from chart data and spec */
export interface ChartSignals {
  intent?: 'comparison' | 'ranking' | 'trend' | 'composition' | 'distribution' | 'correlation' | 'flow' | 'kpi' | 'multivariate' | 'part_of_whole' | 'incremental';
  variableType?: 'nominal' | 'ordinal' | 'continuous';
  numCategories: number;
  numSeries: number;
  numDataPoints: number;
  labelLength?: 'short' | 'long';
  hasNegatives: boolean;
  isTimeAxis?: boolean;
  audience?: 'executive' | 'analyst' | 'general';
  numVariables?: number;
}

/** Frontend ChartData type (subset we can recommend / override to) */
export type RecommendableChartType =
  | 'bar'
  | 'horizontal_bar'
  | 'line'
  | 'area'
  | 'pie'
  | 'donut'
  | 'sunburst'
  | 'scatter'
  | 'bubble'
  | 'gauge'
  | 'treemap'
  | 'funnel'
  | 'waterfall'
  | 'radar'
  | 'heatmap'
  | 'histogram'
  | 'boxplot'
  | 'multi_column_bar'
  | 'stacked_bar'
  | 'candlestick';

const PIE_MAX_CATEGORIES = 6;
const PIE_MIN_CATEGORIES = 2;
const GAUGE_SINGLE_VALUE = 1;
const MANY_CATEGORIES_THRESHOLD = 12;
const VERY_MANY_CATEGORIES = 40;

/**
 * Derive chart signals from raw backend chart data and chart_type.
 * Use originalCount when backend sends data_limiting.original_count (e.g. 40+ categories) so we override based on full size.
 */
export function deriveSignalsFromChart(
  data: any[],
  chartType: string,
  isMultiSeries?: boolean,
  originalCount?: number
): ChartSignals {
  const lower = (chartType || '').toLowerCase();
  const n = data?.length ?? 0;
  const numCategories = originalCount != null && originalCount > n ? originalCount : n;

  let numSeries = 1;
  let hasNegatives = false;
  let maxLabelLen = 0;

  if (Array.isArray(data) && data.length > 0) {
    const first = data[0];
    const keys = Object.keys(first);
    const numericKeys = keys.filter((k) => typeof first[k] === 'number');
    numSeries = isMultiSeries ? Math.max(1, numericKeys.length) : 1;

    for (const row of data) {
      for (const k of numericKeys) {
        const v = row[k];
        if (typeof v === 'number' && v < 0) hasNegatives = true;
      }
      const nameVal = row.name ?? row.label ?? row.category ?? row[keys[0]];
      if (nameVal != null) {
        const len = String(nameVal).length;
        if (len > maxLabelLen) maxLabelLen = len;
      }
    }
  }

  const isTimeAxis =
    lower.includes('line') ||
    lower.includes('time') ||
    lower.includes('trend') ||
    (data?.length > 0 && isLikelyTimeField(data));

  return {
    numCategories,
    numSeries,
    numDataPoints: n,
    hasNegatives,
    labelLength: maxLabelLen > 20 ? 'long' : 'short',
    isTimeAxis,
    audience: 'general',
    numVariables: numSeries,
  };
}

function isLikelyTimeField(data: any[]): boolean {
  if (!data.length) return false;
  const first = data[0];
  const nameVal = first.name ?? first.label ?? first.date ?? first.category ?? Object.values(first)[0];
  const s = String(nameVal ?? '');
  return /^\d{4}-\d{2}-\d{2}|^\d{1,2}\/\d{1,2}\/\d{2,4}|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|Q1|Q2|Q3|Q4/i.test(s);
}

/**
 * Map backend chart_type string to our recommendable type key (for rules).
 */
function backendChartTypeToKey(chartType: string): RecommendableChartType | null {
  const t = (chartType || '').toLowerCase();
  if (t.includes('scatter')) return 'scatter';
  if (t.includes('bubble')) return 'bubble';
  if (t.includes('dual_axis')) return 'line';
  if (t.includes('radar') || t.includes('spider')) return 'radar';
  if (t.includes('sensitivity')) return 'heatmap';
  if (t.includes('correlation')) return 'heatmap';
  if (t.includes('heatmap')) return 'heatmap';
  if (t.includes('treemap')) return 'treemap';
  if (t.includes('sunburst')) return 'sunburst';
  if (t.includes('funnel')) return 'funnel';
  if (t.includes('sparkline')) return 'gauge';
  if (t.includes('bullet')) return 'gauge';
  if (t.includes('gauge') || t.includes('kpi')) return 'gauge';
  if (t.includes('candlestick') || t.includes('kline')) return 'candlestick';
  if (t.includes('waterfall')) return 'waterfall';
  if (t.includes('histogram')) return 'histogram';
  if (t.includes('box')) return 'boxplot';
  if (t.includes('area')) return 'area';
  if (t.includes('donut')) return 'donut';
  if (t.includes('pie') || t.includes('circular')) return 'pie';
  if (t.includes('line') || t.includes('time')) return 'line';
  if (t.includes('multi_column')) return 'multi_column_bar';
  if (t.includes('stacked_bar')) return 'stacked_bar';
  if (t.includes('horizontal_bar')) return 'horizontal_bar';
  if (t.includes('diverging_bar')) return 'bar';
  if (t.includes('bar') || t.includes('column')) return 'bar';
  return 'bar';
}

/**
 * Decide whether to override the backend-suggested chart type based on data signals.
 * Returns the overriding type, or null if the backend choice is acceptable.
 */
export function getOverrideChartType(
  backendChartType: string,
  signals: ChartSignals
): RecommendableChartType | null {
  const suggested = backendChartTypeToKey(backendChartType);
  const suggestedKey: string | null = suggested;
  const { numCategories, numDataPoints, numSeries, hasNegatives } = signals;

  // Single value → bar (line/gauge don't work well; bar shows one bar, pie would show one slice)
  if (numDataPoints === GAUGE_SINGLE_VALUE || numCategories === GAUGE_SINGLE_VALUE) {
    if (suggested === 'line' || suggested === 'area' || suggested === 'gauge') return 'bar';
    return null;
  }

  // Pie/donut: only allow when 2–6 categories; otherwise override
  if (suggested === 'pie' || suggested === 'donut') {
    if (numCategories > PIE_MAX_CATEGORIES) {
      if (numCategories > MANY_CATEGORIES_THRESHOLD) return 'horizontal_bar';
      return 'horizontal_bar';
    }
    if (numCategories < PIE_MIN_CATEGORIES && numCategories > 0) return 'bar';
    return null;
  }

  // Gauge suggested but multiple values → use bar or horizontal_bar
  if (suggested === 'gauge' && (numCategories > 1 || numDataPoints > 1)) {
    return numCategories > MANY_CATEGORIES_THRESHOLD ? 'horizontal_bar' : 'bar';
  }

  // Many categories (e.g. >12): prefer horizontal_bar or treemap; avoid pie (already handled), avoid vertical bar
  if (numCategories > MANY_CATEGORIES_THRESHOLD) {
    if (suggested === 'bar') return 'horizontal_bar';
    if (suggestedKey === 'pie' || suggestedKey === 'donut') return 'horizontal_bar';
  }

  // Very many (40+): only chart types that handle many categories well
  if (numCategories >= VERY_MANY_CATEGORIES) {
    if (['pie', 'donut', 'radar', 'gauge', 'funnel'].includes(suggested ?? '')) return 'horizontal_bar';
    if (suggested === 'bar') return 'horizontal_bar';
  }

  // Negative values: avoid pie/treemap/funnel
  if (hasNegatives && (suggestedKey === 'pie' || suggestedKey === 'donut' || suggested === 'treemap' || suggested === 'funnel')) {
    return 'bar';
  }

  return null;
}
